import React, { useState } from "react"
import { useDispatch } from 'react-redux'
import { Button, CustomInput, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { LabeledTextInput } from 'web/components/generic/LabeledTextInput'
import { FightParserError, parseFightOutline } from 'web/data/fight-outline-importer'
import { importSetup } from 'web/data/setup-import-export'
import { useCatchError } from 'web/hooks/useCatchError'
import { useInputValue } from 'web/hooks/useInputValue'
import { useKeyShortcut } from 'web/hooks/useKeyShortcut'
import { DisplayFightActions } from 'web/redux/slices/display-fight-slice'
import { FightActions } from 'web/redux/slices/fight-slice'
import { SetupActions } from 'web/redux/slices/setup-slice'
import { ModalContainerProps } from "./_ModalContainer"
import { parseViserioOutline } from 'web/data/viserio-outline-importer'
import { dataTest } from 'web/utils/test-utils'
import { FIGHT_CATEGORY_ID } from 'web/data/general/id'
import { createGroupedOptions } from 'web/utils/jsx-utils'
import { GuiData } from 'web/data/gui-data'

export const ImportFightModal: React.FC<ModalContainerProps> = ({ closeSelf }) => {

    const catchError = useCatchError()
    const disp = useDispatch()

    const [parseError, setParseError] = useState('')
    const [fightName, onFightNameChange] = useInputValue('')
    const [fightString, onFightStringChange] = useInputValue('')

    const [fightCategory, setFightCategory] = useState(FIGHT_CATEGORY_ID.CUSTOM.Uncategorized)

    const fightCategoryOptions = createGroupedOptions({
        optionGroups: GuiData.FightCategoryOptions,
        onlyIncludeSelectable: true
    })

    const doImport = () => {

        /*
            TODO

            Category selection is implemented horribly, I've hacked it
            in for text/viserio imports, and just ignore it on pure
            exports - It needs to be implemented properly somehow,
            don't have time now though.
         */

        const fightNameValue = (fightName.trim() === '') ? undefined : fightName.trim()

        if (fightString === "") {
            setParseError("Enter something...")
            return
        }

        if (fightString.startsWith("!viserio")) {
            const { fight, segments, events } = parseViserioOutline({
                fightOutlineString: fightString,
                fightName: fightNameValue,
            })

            fight.categoryId = fightCategory
            disp(FightActions.addNewFight({ fight, segments, events }))
            disp(DisplayFightActions.selectFight({ fightId: fight.id }))

            closeSelf()
            return
        }

        if (fightString.startsWith("export[")) {
            const importData = importSetup({ exportStr: fightString })

            disp(FightActions.addNewFight({
                ...importData.fullFight,
                fight: {
                    ...importData.fullFight.fight,
                    name: fightName !== ''
                        ? fightName
                        : importData.fullFight.fight.name
                }
            }))

            if (importData.setup) {
                disp(SetupActions.addNewSetup({
                    setup: importData.setup
                }))
            }

            disp(DisplayFightActions.selectFight({ fightId: importData.fullFight.fight.id }))
            closeSelf()
            return
        }

        try {

            const { fight, segments, events } = parseFightOutline({
                fightOutlineString: fightString,
                fightName: fightNameValue
            })

            fight.categoryId = fightCategory
            disp(FightActions.addNewFight({ fight, segments, events }))
            disp(DisplayFightActions.selectFight({ fightId: fight.id }))

            closeSelf()

        } catch (error) {
            if (error instanceof FightParserError) {

                switch (error.code) {
                    case FightParserError.ErrorCode.EMPTY:
                        setParseError(`You must enter something`)
                        break
                    case FightParserError.ErrorCode.EMPTY_SEGMENT:
                        setParseError(`Found an empty segment, segments must be followed by at least one event:\n${error.trace}`)
                        break
                    case FightParserError.ErrorCode.MALFORMED_LINE:
                        setParseError(`Found a malformed line:\n${error.trace}`)
                        break
                    case FightParserError.ErrorCode.MALFORMED_SEGMENT_ILLEGAL_TIMESTAMP:
                        setParseError(`The first segment cannot have a timestamp`)
                        break
                    case FightParserError.ErrorCode.NOT_CHRONOLOGICAL:
                        setParseError(`All segments and events must be chronological:\n${error.trace}`)
                        break

                    default:
                        throw Error(`Unhandled parser error: ${error.code}`)

                }

            } else {
                throw error
            }
        }
    }

    useKeyShortcut({
        key: "s",
        modifier: "ctrl",
        callback: doImport,
    })

    return (
        <Modal
            isOpen
            fade={false}
            size="lg"
            toggle={closeSelf}
            backdrop={"static"}
        >
            <ModalHeader>
                {"Import fight"}
            </ModalHeader>

            <ModalBody className="p-3 m-0 bg-light">

                <div className="d-flex space-mx-2 mb-3 align-items-end">
                    <LabeledTextInput
                        small
                        label="Fight name (optional)"
                        value={fightName}
                        onChange={onFightNameChange}
                        className="flex-grow-1"
                    />

                    <FormGroup className="mb-0 small">
                        <Label>{"Fight category"}</Label>
                        <CustomInput
                            data-test={dataTest('edit-fight-category-select')}
                            id="fight-category-select"
                            bsSize="sm"
                            type="select"
                            onChange={e => setFightCategory(e.target.value)}
                            value={fightCategory}
                        >
                            {fightCategoryOptions}
                        </CustomInput>
                    </FormGroup>
                </div>

                <FormGroup className="mb-0 small">
                    <Label>{"Import string"}</Label>
                    <Input
                        bsSize="sm"
                        type="textarea"
                        value={fightString}
                        onChange={(e) => {
                            onFightStringChange(e)
                            setParseError('')
                        }}
                        className={"text-monospace"}
                        style={{
                            minHeight: "500px"
                        }}
                        invalid={parseError !== ''}
                    />
                </FormGroup>

                {parseError !== '' &&
                    <pre className="mt-3 d-block invalid-feedback">
                        {parseError}
                    </pre>
                }
            </ModalBody>

            <ModalFooter className="d-flex justify-content-end space-mx-2" >
                <Button
                    outline
                    size="sm"
                    onClick={catchError(doImport)}
                    title={"Import (ctrl+s)"}
                >
                    {"Import"}
                </Button>

                <Button
                    outline
                    size="sm"
                    onClick={catchError(closeSelf)}
                    title={"Cancel (esc)"}
                >
                    {"Cancel"}
                </Button>
            </ModalFooter>
        </Modal>
    )
}
